<script lang="ts" setup>
import PalkLogoColor from "@/components/PalkLogoColor.vue";
import {computed, watch} from "vue";
import type {LoginBanner} from "@/features/login-banner/LoginBanner.ts";
import {useFetch} from "@/api/composables/useFetch.ts";
import {sanitizeHtml} from "@/utils/sanitizeHtml.ts";

import {LoginBannerEndpoints} from "@/features/login-banner/LoginBannerEndpoints.ts";

defineOptions({
  name: 'LoginBanner',
})

const {
  data: model,
  isLoading,
  error,
} = useFetch<LoginBanner>(LoginBannerEndpoints.GET);

const redirect = () => {
  if (model.value != null && model.value.url) {
    window.open(model.value.url, '_blank');
  }
}

const sanitizedText = computed(() => sanitizeHtml(model.value?.text));

// Block reveal of .content-hidden (login card + banner) until the
// /api/loginbanner fetch settles — success or error. This prevents the
// login card from flashing in before the banner image is ready next to it.
// Error path also unblocks so a failing fetch cannot leave the page
// permanently invisible.
watch(
  [isLoading, model, error],
  () => {
    if (isLoading.value) return;
    if (model.value == null && error.value == null) return;
    if (typeof window.signalVueComponentReady === 'function') {
      window.signalVueComponentReady();
    }
  },
  { immediate: true }
);


</script>

<template>
  <!-- Always render the 600×590 wrapper so the flex slot is held while the
       /api/loginbanner fetch is in flight. Without this the slot collapses
       and the login card sits wider/off-center until the banner pops in.
       The default banner image fills the slot during load — the same image
       that would render for accounts without a custom banner — so no
       visible state change when the model arrives unless a custom image
       overrides it. The custom image is now loaded by URL (browser-cached)
       rather than inlined as base64, so the JSON metadata stays tiny. -->
  <div :style="model && model.imageUrl ? { backgroundImage: `url('${model.imageUrl}')` } : {}"
       :class="['login-banner', { 'default-banner-image': !model || !model.imageUrl }]"
       @click="redirect"
  >
    <template v-if="model">
      <div class="tagline-div" v-html="sanitizedText"></div>
      <div v-if="!model.logoDisabled" class="logo-div">
        <palk-logo-color/>
      </div>
    </template>
  </div>
</template>

<style scoped>
.login-banner {
  display: block;
  width: 600px;
  max-width: 600px;
  max-height: 590px;
  height: 590px;
  border-radius: 5px;
  position: relative;
  background-size: cover;
  background-size: calc(600px);
  cursor: pointer;
}

.default-banner-image {
  background: #222222 url('@/assets/login-banner-default.png') no-repeat center;
}

.tagline-div {
  padding: 50px;
  color: #fff;
  font-size: 36px;
  line-height: 46px;
  max-width: 550px;
}

.tagline-div ::v-deep(blockquote p),
.tagline-div ::v-deep(p) {
  margin-block-start: 10px;
  margin-block-end: 10px;
}

.logo-div {
  position: absolute;
  background-color: #fff;
  height: 51px;
  width: 176px;
  bottom: 39px;
  right: 0;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;

  display: flex;
  justify-content: center;
  align-items: center;
}

</style>